import React, { lazy, useEffect } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { ShowLoading } from "../utilities/functions";
import ProtectedRoute from "../middleware/ProtectedRoute";
import { endSession } from "../middleware/session";


interface AppRoutesProps { }

const LandingPage = lazy(() => import("../pages/LandingPage/LandingPage"));
//const HomePage = lazy(() => import("../pages/HomePage/HomePage"));
const HomePage = lazy(() => import("../pages/HomePage/HomePageV2"));
const NewQuotePage = lazy(() => import("../pages/NewQuotePage/NewQuotePage"));
const QuoteDetailsPage = lazy(
  () => import("../pages/QuoteDetailsPage/QuoteDetailsPage")
);
const CostBenefitPage = lazy(() => import("../pages/CostBenefitPage/CostBenefitPage"));
const FeedbackPage = lazy(() => import("../pages/QuoteFeedbackPage/QuoteFeedbackPage"));
const PanelPage = lazy(() => import("../pages/PanelPage/PanelPage"));
const ProjectPage = lazy(() => import("../pages/ProjectPage/ProjectPage"));
const ProjectDetailsPage = lazy(() => import("../pages/ProjectDetailsPage/ProjectDetailsPage"));
const ProjectDocumentsPage = lazy(() => import("../pages/ProjectDocumentsPage/ProjectDocumentsPage"));
const ProjectDocumentFormPage = lazy(() => import("../pages/ProjectDocumentsPage/ProjectDocumentFormPage"));
const DesignVerificationPage = lazy(() => import("../pages/DesignVerificationPage/DesignVerificationPage"));
const SubcontractorEngagementPage = lazy(() => import("../pages/SubcontractorEngagementPage/SubcontractorEngagementPage"));
const ProjectChangeRequestPage = lazy(() => import("../pages/ProjectChangeRequest/ProjectChangeRequestPage"));
const ProjectSWMSForm = lazy(() => import("../pages/ProjectSwmsForm/ProjectSWMSForm"));
const ProjectMeeting = lazy(() => import("../pages/ProjectMeeting/ProjectMeeting"));
const ProjectCommentRegisterPage = lazy(() => import("../pages/ProjectCommentRegisterPage/ProjectCommentRegisterPage"));


const OrganisationsPage = lazy(
  () => import("../pages/OrganisationsPage/OrganisationsPage")
);
const UsersPage = lazy(() => import("../pages/UsersPage/UsersPage"));
const UserProfilePage = lazy(
  () => import("../pages/UserProfilePage/UserProfilePage")
);
const ErrorPermissionsPage = lazy(
  () => import("../pages/ErrorPermissionPage/ErrorPermissionPage")
);


const QuoteSettings = lazy(() => import("../pages/SettingsPage/SettingsPage"));
const My404Component = lazy(
  () => import("../pages/ErrorNotFoundPage/ErrorNotFoundPage")
);

//const QuoteSettings = lazy(() => import("../pages/SettingPage/SettingPage"));
const ErrorAccountValidation = lazy(
  () => import("../pages/VerifyAccountPage/verifyAccountPage")
);

// Session control variables
const MAX_SESSION_TIME = 360; // Minutes

const AppRoutes: React.FC<AppRoutesProps> = () => {
  const { user, isAuthenticated, isLoading, logout } = useAuth0();

  const location = useLocation();

  // Log out user and clear the session data (stored preferences are kept)
  function LogOutUser() {
    endSession(logout);
  }

  // Start the session clock on sign-in and log out once MAX_SESSION_TIME has passed, including
  // while the app stays open. Only runs once Auth0 has finished loading: clearing storage while
  // Auth0 is still completing a login wipes its login transaction and sends the user back to login.
  // This is a UI convenience only (the timestamp is user-editable); the real session limit
  // belongs in the Auth0 tenant settings.
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;

    let sessionStart = Number(localStorage.getItem("time"));
    if (!sessionStart) {
      sessionStart = Date.now();
      localStorage.setItem("time", sessionStart.toString());
    }

    const remaining = sessionStart + MAX_SESSION_TIME * 60 * 1000 - Date.now();
    if (remaining <= 0) {
      LogOutUser();
      return;
    }

    const timer = setTimeout(LogOutUser, remaining);
    return () => clearTimeout(timer);
  }, [isLoading, isAuthenticated]);

  // Show while waiting for Auth0 authentication
  if (isLoading) {
    return ShowLoading();
  }

  // If user is registered in Auth0 but email was not verified
  if (isAuthenticated && !user?.email_verified) {
    return (
      <Switch>
        <Redirect path='/home' exact={true} to='/' />;
        <ProtectedRoute
          path='/errorAccountValidation'
          exact={true}
          component={ErrorAccountValidation}
        />
        {/* <ProtectedRoute path="/costBenefit"  exact={true} component={CostBenefitPage }/> */}
        <Redirect to='/errorAccountValidation' />
      </Switch>
    );
  }

  // If user is authenticated by Auth0
  else if (isAuthenticated) {
    return (
      <Switch>
        <Redirect path='/' exact={true} to='/home' />;
        <ProtectedRoute path='/home' exact={true} component={HomePage} />
        <ProtectedRoute path='/projects' exact={true} component={ProjectPage} />
        {/* <ProtectedRoute path='/testhome' exact={true} component={TestHomePage} /> */}
        <ProtectedRoute
          path='/errorAccountValidation'
          exact={true}
          component={ErrorAccountValidation}
        />
        <ProtectedRoute
          path='/newQuote'
          exact={true}
          component={NewQuotePage}
        />
        <ProtectedRoute
          path='/panels'
          exact
          component={PanelPage}
        />
        <ProtectedRoute
          path='/organisations'
          exact
          component={OrganisationsPage}
        />
        <ProtectedRoute
          path='/users'
          exact={true}
          component={UsersPage}
        />
        <ProtectedRoute
          path='/quoteDetails/:quoteId'
          exact
          component={QuoteDetailsPage}
        />
        <ProtectedRoute
          path='/profile'
          exact={true}
          component={UserProfilePage}
        />
        <ProtectedRoute
          path='/quoteSettings'
          exact={true}
          component={QuoteSettings}
        />
        <ProtectedRoute path="/costBenefit/:quoteId" exact={true} component={CostBenefitPage} />
        <ProtectedRoute path="/feedback/:quoteId" exact={true} component={FeedbackPage} />
        <ProtectedRoute
          path='/projectDetails/:projectId'
          exact={true}
          component={ProjectDetailsPage}
        />
        <ProtectedRoute
          path='/projects/:projectId/documents'
          exact={true}
          component={ProjectDocumentsPage}
        />
        <ProtectedRoute
          path='/projects/:projectId/documents/:docType'
          exact={true}
          component={ProjectDocumentFormPage}
        />
        <ProtectedRoute
          path='/projects/:projectId/comment-register'
          exact={true}
          component={ProjectCommentRegisterPage}
        />
        <ProtectedRoute
          path='/projects/:projectId/design-verification'
          exact={true}
          component={DesignVerificationPage}
        />
        <ProtectedRoute
          path='/projects/:projectId/subcontractor-engagement'
          exact={true}
          component={SubcontractorEngagementPage}
        />
        <ProtectedRoute
          path='/projects/:projectId/change-request'
          exact={true}
          component={ProjectChangeRequestPage}
        />
        <ProtectedRoute
          path='/projects/:projectId/meeting'
          exact
          component={ProjectMeeting}
        />
        <ProtectedRoute
          path='/projects/:projectId/swms'
          exact={true}
          component={ProjectSWMSForm}
        />
        <Route
          path='/errorPermission'
          exact={true}
          component={ErrorPermissionsPage}
        />
        <Route path='*' exact={true} component={My404Component} />

      </Switch>
    );
  }




  // If user is not authenticated by Auth0
  else if (!isAuthenticated) {
    return (
      <Switch>
        <Redirect path='/home' exact={true} to='/errorPermission' />;
        <Route
          path='/errorPermission'
          exact={true}
          component={ErrorPermissionsPage}
        />
        <Route path='/' exact={true} component={LandingPage} />
        <Route path='*' exact={true} component={My404Component} />
      </Switch>
    );
  } else {
    return null;
  }
};

export default AppRoutes;
